/**
 * The screener on a touch screen — the pure half (`docs/features/mobile.md`,
 * "The screener on a touch screen"). What the top row names, how a tick in the
 * Universe sheet changes the selection, and how many chips fit one row. Kept
 * here because the component is a gesture surface jsdom cannot lay out.
 */

import type { SymbolGroup } from "../api/symbols";
import { mergeKey, type GroupSelection } from "./group-selection";
import { marketByCode } from "./market";

/** A group as one tick: same-named groups in two markets are one entry, two codes. */
export interface MergedGroup extends SymbolGroup {
  codes: string[];
}

/**
 * Collapse groups that are the same entry in two markets into one — used by the
 * desktop `GroupBar` and the touch Universe sheet alike.
 *
 * Keyed on type plus name, which is exactly what collides and nothing else does
 * — measured on the live catalog, five names of 81 appear in two markets. The
 * count is **summed**: a merged "Large Cap" holding 100 US and 100 Indian names
 * has 200 members. Null `resolvedAt` survives a merge, because a pair where one
 * half has not been built is not a resolved group.
 */
export function mergeAcrossMarkets(list: readonly SymbolGroup[]): MergedGroup[] {
  const merged = new Map<string, MergedGroup>();
  for (const g of list) {
    const key = mergeKey(g.groupType, g.name);
    const seen = merged.get(key);
    if (seen) {
      seen.codes.push(g.code);
      seen.memberCount += g.memberCount;
      if (g.resolvedAt === null) seen.resolvedAt = null;
    } else {
      merged.set(key, { ...g, codes: [g.code] });
    }
  }
  return [...merged.values()];
}

/** One universe as the top row and the dropdown name it. */
export interface UniverseLabel {
  groupType: string;
  code: string;
  name: string;
  count: number;
}

/**
 * The universes being screened, in the order they were picked — switched-off
 * ones left out, since they are not in the screen. A name shared by two markets
 * carries the market, as the desktop chip does.
 */
export function appliedUniverses(
  selection: GroupSelection,
  groupsByType: ReadonlyMap<string, readonly SymbolGroup[]>,
): UniverseLabel[] {
  const find = (groupType: string, code: string) =>
    (groupsByType.get(groupType) ?? []).find((g) => g.code === code);
  const picked =
    selection.view === "single"
      ? selection.group
        ? [{ groupType: selection.groupType, code: selection.group }]
        : []
      : selection.picked.filter((p) => p.on);
  const names = picked.map((p) => find(p.groupType, p.code)?.name ?? p.code);
  return picked.map((p, i) => {
    const g = find(p.groupType, p.code);
    const shared = names.filter((n) => n === names[i]).length > 1;
    const market = shared && g?.market ? ` · ${marketByCode(g.market).label}` : "";
    return { groupType: p.groupType, code: p.code, name: `${names[i]}${market}`, count: g?.memberCount ?? 0 };
  });
}

/** Whether a merged entry is ticked: any of its codes picked (and on, in Multi). */
export function isTicked(selection: GroupSelection, groupType: string, codes: readonly string[]): boolean {
  if (selection.view === "single") return selection.groupType === groupType && codes.includes(selection.group ?? "");
  return selection.picked.some((p) => p.groupType === groupType && codes.includes(p.code));
}

/**
 * A tick in the Universe sheet.
 *
 * Multi Select adds or removes **every code the entry stands for** — the
 * desktop's `togglePicked`, for its reason: a merged entry is one tick and two
 * universes. Single Select makes it the one group (and its type the shown one);
 * ticking the group already chosen clears it.
 */
export function tickUniverse(
  selection: GroupSelection,
  groupType: string,
  codes: readonly string[],
): GroupSelection {
  if (selection.view === "single") {
    const on = isTicked(selection, groupType, codes);
    return { ...selection, groupType, group: on ? null : (codes[0] ?? null) };
  }
  const existing = selection.picked.some((p) => p.groupType === groupType && codes.includes(p.code));
  return {
    ...selection,
    picked: existing
      ? selection.picked.filter((p) => !(p.groupType === groupType && codes.includes(p.code)))
      : [
          ...selection.picked,
          ...codes
            .filter((code) => !selection.picked.some((p) => p.groupType === groupType && p.code === code))
            .map((code) => ({ groupType, code, on: true })),
        ],
  };
}

/** Take one universe out — the ✕ on a chip in the sheet. */
export function removeUniverse(selection: GroupSelection, groupType: string, code: string): GroupSelection {
  if (selection.view === "single") return selection.group === code ? { ...selection, group: null } : selection;
  return { ...selection, picked: selection.picked.filter((p) => !(p.groupType === groupType && p.code === code)) };
}

/**
 * How many chips of `widths` fit in `available` px with `gap` between them,
 * leaving room for a `+N▾` button `plus` px wide whenever any are left over.
 * At least one is always shown, so the row never reads as empty.
 */
export function chipsThatFit(widths: readonly number[], available: number, gap: number, plus: number): number {
  const total = widths.reduce((s, w) => s + w, 0) + gap * Math.max(0, widths.length - 1);
  if (total <= available) return widths.length;
  let used = 0;
  for (let i = 0; i < widths.length; i++) {
    const next = used + (i > 0 ? gap : 0) + widths[i]!;
    if (next + gap + plus > available) return Math.max(1, i);
    used = next;
  }
  return widths.length;
}
